import { injectable } from 'inversify';

import {
  DROPPABLE_SECTION_PREFIX,
  DASHBOARD_EMPTY_CELL_ID_PREFIX,
} from '../../constants';
import {
  ComputeGrid,
  DashboardGridItem,
  SectionDragState,
  SectionKey,
  Widget,
  WidgetDropTarget,
} from '../../types';

import { DashboardGridService } from './dashboardGrid.service';
import { DashboardSectionsService } from './sections.service';

const NO_TARGET = {
  targetIndex: -1,
  shiftTargetRow: -1,
};

@injectable()
export class DashboardDragAndDropService {
  constructor(
    private readonly gridService: DashboardGridService,
    private readonly sectionsService: DashboardSectionsService,
  ) {}

  /** Обрабатывает завершение перетаскивания — возвращает обновлённые виджеты или null */
  processDrop(
    activeId: string,
    activeWidgets: Widget[],
    sectionsConfig: Record<SectionKey, { cols: number }>,
    itemFromAvailable?: Widget,
    overId?: string,
  ) {
    if (!overId) return null;

    if (itemFromAvailable) {
      const targetSection = this.sectionsService.getSectionForWidgetType(
        itemFromAvailable.type,
      );
      const overSection = this.getOverSection(overId, activeWidgets);

      if (overSection !== targetSection) return null;

      return {
        widgets: this.applyAddWidget(
          activeWidgets,
          sectionsConfig,
          itemFromAvailable,
          targetSection,
          overId,
        ),
        activeWidgetId: itemFromAvailable.id,
      };
    }

    if (activeId === overId) return null;

    const activeWidget = activeWidgets.find((w) => w.id === activeId);
    if (!activeWidget) return null;

    const activeSection = this.sectionsService.getSectionForWidgetType(
      activeWidget.type,
    );

    if (overId.startsWith(DASHBOARD_EMPTY_CELL_ID_PREFIX)) {
      const parsed = this.parseEmptyCellId(overId);
      if (parsed && parsed.section === activeSection) {
        const updated = this.applyMoveWidgetToCell(
          activeWidgets,
          sectionsConfig,
          activeId,
          activeSection,
          parsed.index,
        );
        if (updated) {
          return { widgets: updated, activeWidgetId: activeId };
        }
      }
      return null;
    }

    if (overId.startsWith(DROPPABLE_SECTION_PREFIX)) {
      const overSection = overId.slice(DROPPABLE_SECTION_PREFIX.length);
      if (activeSection !== overSection) return null;

      return {
        widgets: this.applyReorderInSection(
          activeWidgets,
          sectionsConfig,
          activeId,
          activeSection,
          -1,
        ),
        activeWidgetId: activeId,
      };
    }

    const overWidget = activeWidgets.find((w) => w.id === overId);
    if (!overWidget) return null;

    const overSection = this.sectionsService.getSectionForWidgetType(
      overWidget.type,
    );
    if (activeSection !== overSection) return null;

    return {
      widgets: this.applyMoveWidgetToWidget(
        activeWidgets,
        sectionsConfig,
        activeId,
        activeSection,
        overId,
      ),
      activeWidgetId: activeId,
    };
  }

  /** Парсит id пустой ячейки в формат - section/index */
  parseEmptyCellId(id: string): { section: string; index: number } | null {
    if (!id.startsWith(DASHBOARD_EMPTY_CELL_ID_PREFIX)) return null;
    const rest = id.slice(DASHBOARD_EMPTY_CELL_ID_PREFIX.length);
    const dashIndex = rest.lastIndexOf('-');
    if (dashIndex === -1) return null;

    const section = rest.slice(0, dashIndex);
    const index = parseInt(rest.slice(dashIndex + 1), 10);
    if (isNaN(index)) return null;

    return { section, index };
  }

  /** Определяет секцию, над которой произошёл drop */
  getOverSection(overId: string, activeWidgets: Widget[]): SectionKey | null {
    if (overId.startsWith(DASHBOARD_EMPTY_CELL_ID_PREFIX)) {
      const parsed = this.parseEmptyCellId(overId);
      return (parsed?.section as SectionKey) ?? null;
    }
    if (overId.startsWith(DROPPABLE_SECTION_PREFIX)) {
      return overId.slice(DROPPABLE_SECTION_PREFIX.length) as SectionKey;
    }
    const overWidget = activeWidgets.find((w) => w.id === overId);
    if (overWidget) {
      return this.sectionsService.getSectionForWidgetType(overWidget.type);
    }
    return null;
  }

  /** Преобразует dnd-kit overId в WidgetDropTarget */
  resolveDropTarget(
    overId: string | undefined,
    sectionKey: SectionKey,
  ): WidgetDropTarget {
    if (!overId) return { type: 'section' };
    if (overId.startsWith(DASHBOARD_EMPTY_CELL_ID_PREFIX)) {
      const parsed = this.parseEmptyCellId(overId);
      if (parsed && parsed.section === sectionKey) {
        return { type: 'cell', index: parsed.index };
      }
      return { type: 'section' };
    }
    if (overId.startsWith(DROPPABLE_SECTION_PREFIX)) {
      return { type: 'section' };
    }
    return { type: 'widget', widgetId: overId };
  }

  /** Разделяет виджеты на секции */
  private splitBySection(
    allWidgets: Widget[],
    sectionKey: SectionKey,
    sectionsConfig: Record<SectionKey, { cols: number }>,
  ): { sectionWidgets: Widget[]; otherWidgets: Widget[]; cols: number } {
    const sectionWidgets = allWidgets.filter(
      (w) =>
        this.sectionsService.getSectionForWidgetType(w.type) === sectionKey,
    );
    const otherWidgets = allWidgets.filter(
      (w) =>
        this.sectionsService.getSectionForWidgetType(w.type) !== sectionKey,
    );
    const cols = sectionsConfig[sectionKey].cols;
    return { sectionWidgets, otherWidgets, cols };
  }

  /** Применяет операцию к секции и объединяет результат */
  private applySectionOperation(
    allWidgets: Widget[],
    sectionKey: SectionKey,
    sectionsConfig: Record<SectionKey, { cols: number }>,
    operation: (sectionWidgets: Widget[], cols: number) => Widget[],
  ): Widget[] {
    const { sectionWidgets, otherWidgets, cols } = this.splitBySection(
      allWidgets,
      sectionKey,
      sectionsConfig,
    );
    const updated = operation(sectionWidgets, cols);
    return [...otherWidgets, ...updated];
  }

  /** Перемещает виджет на позицию другого виджета внутри секции */
  applyMoveWidgetToWidget(
    allWidgets: Widget[],
    sectionsConfig: Record<SectionKey, { cols: number }>,
    activeId: string,
    sectionKey: SectionKey,
    overId: string,
  ): Widget[] {
    return this.applySectionOperation(
      allWidgets,
      sectionKey,
      sectionsConfig,
      (sectionWidgets, cols) =>
        this.sectionsService.moveWidgetToWidgetPosition(
          sectionWidgets,
          activeId,
          overId,
          cols,
        ),
    );
  }

  /** Добавляет новый виджет в секцию */
  applyAddWidget(
    allWidgets: Widget[],
    sectionsConfig: Record<SectionKey, { cols: number }>,
    widget: Widget,
    sectionKey: SectionKey,
    overId?: string,
  ): Widget[] {
    return this.applySectionOperation(
      allWidgets,
      sectionKey,
      sectionsConfig,
      (sectionWidgets, cols) => {
        const target = this.resolveDropTarget(overId, sectionKey);
        return this.sectionsService.addWidgetToSection(
          sectionWidgets,
          { ...widget, isActive: true },
          target,
          cols,
        );
      },
    );
  }

  /** Перемещает виджет на указанную ячейку внутри секции */
  applyMoveWidgetToCell(
    allWidgets: Widget[],
    sectionsConfig: Record<SectionKey, { cols: number }>,
    activeId: string,
    sectionKey: SectionKey,
    targetIndex: number,
  ): Widget[] | null {
    const { sectionWidgets, otherWidgets, cols } = this.splitBySection(
      allWidgets,
      sectionKey,
      sectionsConfig,
    );
    const updated = this.sectionsService.moveWidgetToCell(
      sectionWidgets,
      activeId,
      targetIndex,
      cols,
    );
    if (!updated) return null;
    return [...otherWidgets, ...updated];
  }

  /** Переставляет виджет в конец секции или на позицию другого виджета */
  applyReorderInSection(
    allWidgets: Widget[],
    sectionsConfig: Record<SectionKey, { cols: number }>,
    activeId: string,
    sectionKey: SectionKey,
    overIdOrEnd: string | -1,
  ): Widget[] {
    return this.applySectionOperation(
      allWidgets,
      sectionKey,
      sectionsConfig,
      (sectionWidgets, cols) =>
        this.sectionsService.reorderWidgets(
          sectionWidgets,
          activeId,
          overIdOrEnd,
          cols,
        ),
    );
  }

  /** Удаляет виджет из сетки */
  applyRemoveWidget(
    allWidgets: Widget[],
    sectionsConfig: Record<SectionKey, { cols: number }>,
    widgetId: string,
  ): Widget[] {
    const widget = allWidgets.find((w) => w.id === widgetId);
    if (!widget) return allWidgets;

    const section = this.sectionsService.getSectionForWidgetType(widget.type);
    return this.applySectionOperation(
      allWidgets,
      section,
      sectionsConfig,
      (sectionWidgets, cols) =>
        this.sectionsService.removeWidgetFromSection(
          sectionWidgets,
          widgetId,
          cols,
        ),
    );
  }

  /** Вычисляет позицию превью при перетаскивании виджета над секцией */
  computeTarget(
    sectionKey: SectionKey,
    activeSection: SectionKey,
    displayGrid: DashboardGridItem[],
    cols: number,
    dragState: SectionDragState,
  ) {
    const {
      isDraggingItem,
      activeItem,
      currentOverId,
      isDraggingFromAvailable,
    } = dragState;

    if (
      !isDraggingItem ||
      !activeItem ||
      activeSection !== sectionKey ||
      !currentOverId
    ) {
      return NO_TARGET;
    }

    const overId = String(currentOverId);
    const computeGrid: ComputeGrid = { displayGrid, cols, activeItem };

    if (overId.startsWith(DASHBOARD_EMPTY_CELL_ID_PREFIX)) {
      return this.computeTargetForEmptyCell(
        overId,
        sectionKey,
        computeGrid,
        isDraggingFromAvailable,
      );
    }

    if (overId.startsWith(DROPPABLE_SECTION_PREFIX)) {
      return this.computeTargetForSection(overId, sectionKey, computeGrid);
    }

    return this.computeTargetForWidget(overId, computeGrid);
  }

  /** Вычисляет превью при наведении на пустую ячейку */
  private computeTargetForEmptyCell(
    overId: string,
    sectionKey: SectionKey,
    computeGrid: ComputeGrid,
    isDraggingFromAvailable: boolean,
  ) {
    const { displayGrid, cols, activeItem } = computeGrid;

    const parsed = this.parseEmptyCellId(overId);
    if (!parsed || parsed.section !== sectionKey) return NO_TARGET;
    const parsedIndex = parsed.index;
    const cell = displayGrid[parsedIndex];
    if (!cell) return NO_TARGET;

    if (cell.type === 'occupied') {
      const cursorRow = Math.floor(parsedIndex / cols);

      const dropIndex = this.gridService.findNearestDropPosition(
        parsedIndex,
        displayGrid,
        cols,
        activeItem.size,
      );

      const resultGrid = this.gridService.insertWidgetAt(
        displayGrid,
        activeItem,
        dropIndex,
        cols,
      );

      if (this.areWidgetsPreserved(displayGrid, resultGrid)) {
        const compactRow = Math.floor(
          this.gridService.findWidgetIndex(resultGrid, activeItem.id) / cols,
        );

        if (compactRow <= cursorRow) {
          return this.compactTarget(resultGrid, activeItem);
        }
      }

      return this.shiftedTarget(parsedIndex, cols, activeItem);
    }

    const dropIndex = this.gridService.findNearestDropPosition(
      parsedIndex,
      displayGrid,
      cols,
      activeItem.size,
    );

    const cursorRow = Math.floor(parsedIndex / cols);
    const dropRow = Math.floor(dropIndex / cols);

    if (dropRow > cursorRow) {
      return this.shiftedTarget(parsedIndex, cols, activeItem);
    }

    const resultGrid = this.gridService.insertWidgetAt(
      displayGrid,
      activeItem,
      dropIndex,
      cols,
    );

    if (isDraggingFromAvailable) {
      return this.compactTarget(resultGrid, activeItem);
    }

    if (!this.areWidgetsPreserved(displayGrid, resultGrid)) return NO_TARGET;

    return this.compactTarget(resultGrid, activeItem);
  }

  /** Вычисляет превью при наведении на секцию */
  private computeTargetForSection(
    overId: string,
    sectionKey: SectionKey,
    computeGrid: ComputeGrid,
  ) {
    const { displayGrid, cols, activeItem } = computeGrid;

    const sectionId = overId.slice(DROPPABLE_SECTION_PREFIX.length);
    if (sectionId !== sectionKey) return NO_TARGET;
    const resultGrid = this.gridService.normalizeGrid(
      [...this.gridService.extractWidgets(displayGrid), activeItem],
      cols,
    );
    const targetIndex = this.gridService.findWidgetIndex(
      resultGrid,
      activeItem.id,
    );
    const adjustedIndex = this.gridService.clampIndexInRow(
      targetIndex,
      cols,
      activeItem.size,
    );

    let canPlaceInDisplayGrid = true;
    for (let i = 0; i < activeItem.size; i++) {
      const cellIndex = adjustedIndex + i;
      if (
        cellIndex >= displayGrid.length ||
        displayGrid[cellIndex].type !== 'empty'
      ) {
        canPlaceInDisplayGrid = false;
        break;
      }
    }

    if (canPlaceInDisplayGrid) {
      return this.compactTarget(resultGrid, activeItem);
    }
    return this.shiftedTarget(adjustedIndex, cols, activeItem);
  }

  /** Вычисляет позицию превью в grid координатах */
  computeHighlightPosition(
    targetIndex: number,
    shiftTargetRow: number,
    cols: number,
    widgetSize: number,
  ): { gridColumn: string; gridRow: number } | null {
    if (targetIndex === -1) return null;

    const highlightRow =
      shiftTargetRow !== -1
        ? shiftTargetRow + 1
        : Math.floor(targetIndex / cols) + 1;

    return {
      gridColumn: `${(targetIndex % cols) + 1} / span ${widgetSize || 1}`,
      gridRow: highlightRow,
    };
  }

  /** Вычисляет превью при наведении на другой виджет */
  private computeTargetForWidget(overId: string, computeGrid: ComputeGrid) {
    const { displayGrid, cols, activeItem } = computeGrid;

    const cardIndex = this.gridService.findWidgetIndex(displayGrid, overId);
    if (cardIndex === -1) return NO_TARGET;

    const dropIndex = this.gridService.findNearestDropPosition(
      cardIndex,
      displayGrid,
      cols,
      activeItem.size,
    );

    const cursorRow = Math.floor(cardIndex / cols);
    const dropRow = Math.floor(dropIndex / cols);

    if (dropRow <= cursorRow) {
      const resultGrid = this.gridService.insertWidgetAt(
        displayGrid,
        activeItem,
        dropIndex,
        cols,
      );

      if (this.areWidgetsPreserved(displayGrid, resultGrid)) {
        return this.compactTarget(resultGrid, activeItem);
      }
    }

    return this.shiftedTarget(cardIndex, cols, activeItem);
  }

  /** Возвращает результат с позицией виджета после компактизации */
  private compactTarget(resultGrid: DashboardGridItem[], activeItem: Widget) {
    return {
      targetIndex: this.gridService.findWidgetIndex(resultGrid, activeItem.id),
      shiftTargetRow: -1,
    };
  }

  /** Возвращает результат со сдвигом строки для визуального превью */
  private shiftedTarget(cursorIndex: number, cols: number, activeItem: Widget) {
    const adjustedIndex = this.gridService.clampIndexInRow(
      cursorIndex,
      cols,
      activeItem.size,
    );

    return {
      targetIndex: adjustedIndex,
      shiftTargetRow: Math.floor(adjustedIndex / cols),
    };
  }

  /** Проверяет, что все виджеты исходной сетки сохранены после вставки */
  private areWidgetsPreserved(
    displayGrid: DashboardGridItem[],
    resultGrid: DashboardGridItem[],
  ): boolean {
    const displayCardIds = this.gridService.getWidgetIds(displayGrid);
    const resultCardIds = this.gridService.getWidgetIds(resultGrid);
    return displayCardIds.every((id) => resultCardIds.includes(id));
  }
}
